import { equalSet as n } from "@allmaps/stdlib";
import { WarpedMapEventType as l, WarpedMapEvent as r } from "../shared/events.js";
import { shouldPruneTile as c } from "../shared/tiles.js";
const o = 4, T = 2;
class U extends EventTarget {
  cachableTileFactory;
  fetchFn;
  tilesByTileUrl = /* @__PURE__ */ new Map();
  mapIdsByTileUrl = /* @__PURE__ */ new Map();
  tileUrlsByMapId = /* @__PURE__ */ new Map();
  tilesFetchingCount = 0;
  fetchableTiles = [];
  constructor(e, t) {
    super(), this.cachableTileFactory = e, this.fetchFn = t?.fetchFn;
  }
  /**
   * Get the tiles in this cache
   *
   * @returns {IterableIterator<CacheableTile>}
   */
  getCacheableTiles() {
    return this.tilesByTileUrl.values();
  }
  /**
   * Get a specific tile in this cache
   *
   * @param {string} tileUrl - the URL of the requested tile
   * @returns {(CacheableTile | undefined)}
   */
  getCacheableTile(e) {
    return this.tilesByTileUrl.get(e);
  }
  /**
   * Get the tiles in this cache, corresponding to a specific map
   *
   * @param {string} mapId - ID of the map
   * @returns {CacheableTile[]}
   */
  getMapCacheableTiles(e) {
    const t = [];
    for (const i of this.tilesByTileUrl.values())
      this.tileUrlsByMapId.get(e)?.has(i.tileUrl) && t.push(i);
    return t;
  }
  /**
   * Get the tiles in this cache that have been fetched
   *
   * @returns {CacheableTile[]}
   */
  getCachedTiles() {
    const e = [];
    for (const t of this.tilesByTileUrl.values())
      t.isCachedTile() && e.push(t);
    return e;
  }
  /**
   * Get a specific cached tile in this cache that has been fetched
   *
   * @param {string} tileUrl - the URL of the requested tile
   * @returns {(CachedTile | undefined)}
   */
  getCachedTile(e) {
    const t = this.tilesByTileUrl.get(e);
    if (t && t.isCachedTile())
      return t;
  }
  /**
   * Get the tiles in this cache, corresponding to a specific map, that have been fetched
   *
   * @param {string} mapId - ID of the map
   * @returns {CachedTile[]}
   */
  getMapCachedTiles(e) {
    const t = [];
    for (const i of this.tilesByTileUrl.values())
      i.isCachedTile() && this.tileUrlsByMapId.get(e)?.has(i.tileUrl) && t.push(i);
    return t;
  }
  /**
   * Get the URLs of tiles in this cache
   *
   * @returns {IterableIterator<string>}
   */
  getTileUrls() {
    return this.tilesByTileUrl.keys();
  }
  /**
   * Get the URLs of tiles in this cache, corresponding to a specific map
   *
   * @param {string} mapId - ID of the map
   * @returns {IterableIterator<string>}
   */
  getMapTileUrls(e) {
    return this.tileUrlsByMapId.get(e) || /* @__PURE__ */ new Set();
  }
  // TODO: this function needs a new name!
  /**
   * Process the request for new tiles to be added to this cache
   *
   * @param {FetchableTile[]} fetchableTiles
   */
  requestFetchableTiles(e) {
    const t = new Set(
      this.fetchableTiles.map((s) => s.fetchableTileKey)
    ), i = new Set(
      e.map((s) => s.fetchableTileKey)
    );
    if (!n(t, i)) {
      for (const s of e)
        this.requestFetchableTile(s);
      this.fetchableTiles = e;
    }
  }
  /**
   * Returns a promise that resolves when all requested tiles are loaded.
   * This could happen immidiately, in case there are no current requests and the tilesFetchingCount is zero,
   * or in a while, when the count reaches zero and the ALLREQUESTEDTILESLOADED event is fired.
   *
   * @async
   * @returns {Promise<void>}
   */
  async allRequestedTilesLoaded() {
    return new Promise((e) => {
      if (this.finished)
        e();
      else {
        const t = () => {
          this.removeEventListener(
            l.ALLREQUESTEDTILESLOADED,
            t
          ), e();
        };
        this.addEventListener(
          l.ALLREQUESTEDTILESLOADED,
          t
        );
      }
    });
  }
  /**
   * Prune tiles in this cache using the provided prune info
   */
  prune(e) {
    for (const [t, i] of this.mapIdsByTileUrl.entries())
      for (const s of i) {
        const a = e.get(s), h = this.tilesByTileUrl.get(t)?.tile;
        h && (!a || c(h, a, {
          maxHigherLog2ScaleFactorDiff: o,
          maxLowerLog2ScaleFactorDiff: T
        })) && this.removeCacheableTileForMapId(t, s);
      }
  }
  clear() {
    for (const e of this.getCacheableTiles())
      e.abort(), this.removeEventListenersFromCacheableTile(e);
    this.tilesByTileUrl = /* @__PURE__ */ new Map(), this.mapIdsByTileUrl = /* @__PURE__ */ new Map(), this.tileUrlsByMapId = /* @__PURE__ */ new Map(), this.tilesFetchingCount = 0;
  }
  destroy() {
    this.clear();
  }
  requestFetchableTile(e) {
    const t = e.mapId, i = e.tileUrl;
    if (this.tilesByTileUrl.has(i))
      this.tilesByTileUrl.get(i)?.isCachedTile() && this.dispatchEvent(
        new r(l.MAPTILELOADED, {
          mapId: t,
          tileUrl: i
        })
      );
    else {
      const s = this.cachableTileFactory(
        e,
        this.fetchFn
      );
      this.addEventListenersToCacheableTile(s), this.addCacheableTile(s);
    }
    this.addTileUrlForMapId(i, t), this.addMapIdForTileUrl(t, i);
  }
  addCacheableTile(e) {
    this.tilesByTileUrl.set(e.tileUrl, e), e.fetch(), this.updateTilesFetchingCount(1);
  }
  removeCacheableTileForMapId(e, t) {
    const i = this.tilesByTileUrl.get(e);
    if (!i)
      return;
    const s = this.removeMapIdForTileUrl(t, e);
    this.removeTileUrlForMapId(e, t), s.size || (i.isCachedTile() || (i.abort(), this.updateTilesFetchingCount(-1)), this.tilesByTileUrl.delete(e)), this.dispatchEvent(
      new r(l.MAPTILEREMOVED, {
        mapId: t,
        tileUrl: e
      })
    );
  }
  tileFetched(e) {
    if (e instanceof r) {
      const t = e.data;
      this.updateTilesFetchingCount(-1);
      for (const i of this.mapIdsByTileUrl.get(t) || [])
        this.dispatchEvent(
          new r(l.MAPTILELOADED, {
            mapId: i,
            tileUrl: t
          })
        ), this.tileUrlsByMapId.get(i)?.values().next().value === t && this.dispatchEvent(
          new r(l.FIRSTMAPTILELOADED, {
            mapId: i,
            tileUrl: t
          })
        );
    }
  }
  tileFetchError(e) {
    if (e instanceof r) {
      const t = e.data;
      this.tilesByTileUrl.has(t) || this.updateTilesFetchingCount(-1);
    }
  }
  addMapIdForTileUrl(e, t) {
    let i = this.mapIdsByTileUrl.get(t);
    return i ? i.add(e) : i = /* @__PURE__ */ new Set([e]), this.mapIdsByTileUrl.set(t, i), i;
  }
  removeMapIdForTileUrl(e, t) {
    const i = this.mapIdsByTileUrl.get(t);
    if (i)
      i.delete(e);
    else
      return /* @__PURE__ */ new Set();
    return i.size ? this.mapIdsByTileUrl.set(t, i) : this.mapIdsByTileUrl.delete(t), i;
  }
  addTileUrlForMapId(e, t) {
    let i = this.tileUrlsByMapId.get(t);
    return i ? i.add(e) : i = /* @__PURE__ */ new Set([e]), this.tileUrlsByMapId.set(t, i), i;
  }
  removeTileUrlForMapId(e, t) {
    const i = this.tileUrlsByMapId.get(t);
    return i ? (i.delete(e), i.size ? this.tileUrlsByMapId.set(t, i) : this.tileUrlsByMapId.delete(t), i) : /* @__PURE__ */ new Set();
  }
  get finished() {
    return this.tilesFetchingCount === 0;
  }
  updateTilesFetchingCount(e) {
    this.tilesFetchingCount += e, this.tilesFetchingCount === 0 && this.dispatchEvent(
      new r(l.ALLREQUESTEDTILESLOADED)
    );
  }
  addEventListenersToCacheableTile(e) {
    e.addEventListener(
      l.TILEFETCHED,
      this.tileFetched.bind(this)
    ), e.addEventListener(
      l.TILEFETCHERROR,
      this.tileFetchError.bind(this)
    );
  }
  removeEventListenersFromCacheableTile(e) {
    e.removeEventListener(
      l.TILEFETCHED,
      this.tileFetched.bind(this)
    ), e.removeEventListener(
      l.TILEFETCHERROR,
      this.tileFetchError.bind(this)
    );
  }
}
export {
  U as default
};
//# sourceMappingURL=TileCache.js.map
